// ============================================================================
// ROLE PERMISSION MODALS — CRS
// ============================================================================
// RolePermissionFormModal — create / edit
// RolePermissionViewModal — read-only details
//
// Mirrors prima-web/src/components/modals/modules/admin-module/RolePermissionModal.tsx
// 1:1 in structure and styling. Module catalog (MODULE_AVAILABLE_ACTIONS,
// MODULE_LABELS, NAV_SECTIONS) is CRS-specific and must stay aligned with:
//   - crs-db/seed/010_seed_admin_role_permissions.sql (DB seed)
//   - login-interface.tsx navigationMenu (sidebar grouping)
//   - DineOps (Restaurant tab): DineOpsPermissions.Modules in crs-api,
//     DINE_MODULES in dineOpsScreens.ts, and the grants in crs-db fixes/042-055

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { RoleOutletUser } from 'api/types';
import RoleOutletAccessSection, {
  type AccessOption,
  type RoleAccessFormState,
} from './RoleOutletAccessSection';

// ============================================================================
// TYPES
// ============================================================================

export interface RolePermissionFormData {
  roleCode: string;
  roleName: string;
  description: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
  /**
   * Role priority for the "highest role wins" resolver. Lower = higher priority.
   * Default 1000 (below every baseline role). Baseline roles use 0/10/40/100.
   * crs_system is UI-locked at 0.
   */
  priority: number;
  /**
   * Restaurant tab → Outlet access (dine.tbl_role_outlet_access). Set once the
   * screen has loaded it; saved after the role itself.
   */
  outletAccess?: RoleAccessFormState;
  /** Restaurant tab → Kitchen departments (dine.tbl_role_station_access): what the kitchen display shows. */
  stationAccess?: RoleAccessFormState;
}

export interface RolePermissionRecord {
  id: string;
  roleCode: string;
  roleName: string;
  description?: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
  isSystemRole: boolean;
  isActive: boolean;
  priority: number;
  createdAt: string;
  createdByName?: string;
  updatedAt?: string;
  updatedByName?: string;
  rowVersion: number;
}

// ============================================================================
// CONSTANTS — CRS module catalog.
// MUST match the menu item IDs in login-interface.tsx → navigationMenu and
// the keys in modulesConfig. The DB seed (crs-db/seed/010) uses these same
// IDs so filterNavigationMenu() can match them at login time.
// ============================================================================

const MODULE_AVAILABLE_ACTIONS: Record<string, string[]> = {
  // Dashboard
  'overview':                     ['view'],
  'dashboard':                    ['view'],
  // Organisation (slice 4b). Writes go through to the organisation master (Tenant
  // Hub); 'edit' also allows Sync, 'view' Verify. No delete — the master retires rows.
  'org-entity':                   ['add', 'edit', 'view'],
  'org-business-unit':            ['add', 'edit', 'view'],
  'org-financial-year':           ['add', 'edit', 'view'],
  // Property Settings
  'property-master':              ['add', 'edit', 'delete', 'view'],
  'room-category-master':         ['add', 'edit', 'delete', 'view'],
  'room-master':                  ['add', 'edit', 'delete', 'view'],
  'meal-plan-master':             ['add', 'edit', 'delete', 'view'],
  'addon-master':                 ['add', 'edit', 'delete', 'view'],
  'property-terms-master':        ['add', 'edit', 'delete', 'view'],
  'bank-account-master':          ['add', 'edit', 'delete', 'view'],
  'tax-master':                   ['add', 'edit', 'delete', 'export', 'view'],
  'tax-registrations':            ['edit', 'view'],
  'setup-checklist':              ['view'],
  'setup-config-report':          ['view', 'export'],
  'pms-charge-type':              ['add', 'edit', 'delete', 'view'],
  // Series rows are created by the system; 'edit' = prefix / digits before the
  // first number of a year, 'submit' = Issue pending numbers (slice 4).
  'pms-document-series':          ['edit', 'submit', 'view'],
  'pms-print-layouts':            ['edit', 'view'],
  'tax-charge-mapping':           ['add', 'edit', 'view'],
  'config-settings':              ['edit', 'view'],
  // Masters
  'agent-master':                 ['add', 'edit', 'delete', 'view'],
  'ota-master':                   ['add', 'edit', 'delete', 'view'],
  'corporate-master':             ['add', 'edit', 'delete', 'view'],
  'rate-plan-master':             ['add', 'edit', 'delete', 'view'],
  // Booking
  // 'arrival-changes' — edit pax / meal plan / extra bed / agreed rate DURING
  // check-in. Deliberately separate from 'checkin' so check-in can be granted
  // widely while rate-affecting changes stay with a supervisor. It does NOT
  // grant booking edit; the server restricts the fields structurally.
  // 'change-settlement' — who pays on an in-house stay, and moving posted charges
  // to the new payer (PMS slice 3b). Decides invoices, so not part of 'edit'.
  'booking-list':                 ['add', 'edit', 'delete', 'view', 'submit', 'checkin', 'checkout', 'mark-no-show', 'cancel', 'extend-stay', 'room-category-change', 'arrival-changes', 'oversell', 'change-settlement'],
  'front-office':                 ['view', 'add', 'checkin', 'checkout', 'mark-no-show', 'extend-stay', 'room-status', 'room-move', 'room-category-change', 'arrival-changes'],
  // In-House Guests + folio (PMS slice 5). 'receive-payment' shows the button; the
  // payment itself is recorded through the payment endpoints as on the booking.
  'pms-inhouse':                  ['view', 'post-charge', 'reverse-charge', 'receive-payment', 'posting-control', 'export',
                                   'post-recovery', 'post-disbursement', 'post-late-charge', 'waive-retention', 'relax-late-checkout',
                                   'meals'],
  // Night audit (PMS slice 6a): submit = run the audit and close the business date.
  'pms-night-audit':              ['view', 'submit'],
  // Audit Corrections (2026-10-14): a check-in / no-show missed on a closed date.
  'audit-corrections':            ['view', 'checkin', 'mark-no-show'],
  // Checkout invoices (PMS slice 7a): issue = the stay's invoices at checkout.
  'pms-invoices':                 ['view', 'issue', 'export', 'credit-note', 'debit-note', 'approve-note'],
  // Front Office daily closing (PMS slice 7d): verify = the Main Cashier's check; reopen needs a reason.
  'fo-closing':                   ['view', 'close', 'verify', 'reopen'],
  'availability':                 ['view'],
  'shortage-board':               ['view', 'plan', 'resolve', 'export'],
  'guest-master':                 ['add', 'edit', 'delete', 'view'],
  // Approvals
  'approval-inbox':               ['view', 'approve'],
  // Administration
  'role-permissions':             ['add', 'edit', 'delete', 'view'],
  'ledger-mapping':               ['add', 'edit', 'delete', 'export'],
  'posting-register':             ['view', 'retry', 'export'],
  // Reports
  'booking-register-report':      ['view', 'export'],
  'complimentary-report':         ['view', 'export'],
  'discount-report':              ['view', 'export'],
  'min-rate-violation-report':    ['view', 'export'],
  'meal-plan-revenue-report':     ['view', 'export'],
  'extra-pax-report':             ['view', 'export'],
  'forward-occupancy-report':     ['view', 'export'],
  'agent-corporate-report':       ['view', 'export'],
  'daily-monthly-summary-report': ['view', 'export'],
  'occupancy-chart-report':       ['view', 'export'],
  'checkin-details-report':       ['view', 'export'],
  // record-accounts-receipt — "Recorded by Accounts": the money is Accounts' liability, out of the desk's closing.
  'advance-payments':             ['edit', 'view', 'export', 'record-accounts-receipt'],
  'collection-report':            ['view', 'export'],
  // Guest Ledger — Posted vs Billed (PMS slice 6c).
  'guest-ledger-report':          ['view', 'export'],
  'waiver-report':                ['view', 'export'],
  'checkout-register':            ['view', 'export'],
  'fo-collection-report':         ['view', 'export'],
  // Buffet check-off slice 4 (crs-db fixes/091).
  'meal-forecast-report':         ['view', 'export'],
  'unused-meal-report':           ['view', 'export'],
  // ---------------------------------------------------------------------------
  // Mobile PWA modules. Actions here MUST match the seed in
  // crs-db/migrations/fixes/018_seed_mobile_modules_in_role_permissions.sql —
  // the DB stores them in the same visible_modules / module_actions columns as
  // the WEB modules above, so this catalog is the single UI-side source.
  // ---------------------------------------------------------------------------
  'MOB-HOME':                     ['view'],
  'MOB-GUEST':                    ['view'],
  'MOB-MEAL':                     ['view'],
  'MOB-OCC':                      ['view'],
  'MOB-SHORT':                    ['view'],
  'MOB-APRV':                     ['view', 'approve', 'reject'],
  'MOB-QBOOK':                    ['view', 'create'],
  // 'view-exec' is the EXECUTIVE league table ("who sold it"), separate because
  // an owner sees individual performance and a GM may not (MIS design decision
  // 8). crs-mobile-api does not even FETCH that block without it.
  'MOB-MIS':                      ['view', 'view-exec'],
  // ---------------------------------------------------------------------------
  // DineOps (Restaurant tab). Same visible_modules / module_actions columns.
  // Actions are every one the DineOps screens, the waiter app and the API check
  // (crs-db fixes/042-055 grant them); some screens check add/edit, others
  // create/update, so both are listed where both are used.
  // ---------------------------------------------------------------------------
  // 'tax' = the outlet's tax profile (DineOps tax T10, crs-db fixes/089): grant to an Accounts role as needed.
  'dineops-outlets':              ['view', 'create', 'add', 'update', 'edit', 'tax'],
  'dineops-departments':          ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-kitchens':             ['view', 'create', 'add', 'update', 'edit', 'delete'],
  'dineops-sections':             ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-tables':               ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-billing-points':       ['view', 'create', 'add', 'update', 'edit', 'delete'],
  'dineops-quick-notes':          ['view', 'create', 'update', 'delete', 'manage'],
  // 'tax' = a dish's tax exception: goods / MRP / outside GST (DineOps tax T10, crs-db fixes/089).
  'dineops-menu':                 ['view', 'create', 'update', 'delete', 'import', 'export', 'manage', 'tax'],
  'dineops-outlet-items':         ['view', 'create', 'add', 'update', 'edit', 'delete'],
  'dineops-price-board':          ['view', 'edit', 'export'],
  'dineops-addon-groups':         ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-tags':                 ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-charges':              ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-specials':             ['view', 'create', 'update', 'delete', 'manage'],
  'dineops-price-lists':          ['view', 'create', 'update', 'delete', 'manage'],
  // 'return' = approve or charge a returned dish (quality, changed mind, other); give it to captains (crs-db fixes/094).
  'dineops-orders':               ['view', 'create', 'update', 'delete', 'send-kot', 'set-price', 'comp', 'return', 'manage'],
  'dineops-kds':                  ['view', 'update', 'reject', 'print', 'manage'],
  // Buffet check-off board in the waiter app (buffet slice 2, crs-db fixes/088): mark includes undo.
  // 'bill' = bill guests outside the plan from the board and charge it to the room (slice 3, fixes/090).
  'dineops-buffet':               ['view', 'mark', 'close', 'bill', 'manage'],
  'dineops-bills':                ['view', 'create', 'update', 'print', 'discount', 'comp', 'void', 'move-bill', 'manage'],
  'dineops-shifts':               ['view', 'create', 'update', 'manage'],
  'dineops-review':               ['view', 'approve', 'reject', 'manage'],
  'dineops-day-close':            ['view', 'day-close', 'manage'],
  // Requisitions R1 (crs-db fixes/093): 'issue' sends to the kitchen and hands over; 'cancel' only before it is sent.
  // 'purposes' maintains the tenant's purpose list (fixes/097).
  'dineops-requisitions':         ['view', 'create', 'add', 'issue', 'cancel', 'purposes'],
  'dineops-requisition-report':   ['view', 'export'],
  // Bill Register (S2, crs-db fixes/104): every bill of a period, the legacy POS register's columns.
  'dineops-bill-register':        ['view', 'export'],
  // Approval Rules (S3, crs-db fixes/105): which restaurant approvals are asked, of which role. Approving needs only the role.
  'dineops-approval-rules':       ['view', 'add', 'edit', 'delete'],
  'dineops-dashboard-report':     ['view', 'print', 'export'],
  'dineops-sales-report':         ['view', 'print', 'export'],
  'dineops-settlement-report':    ['view', 'print', 'export'],
  'dineops-wastage-report':       ['view', 'print', 'export'],
  'dineops-complaints-report':    ['view', 'print', 'export'],
  'dineops-kitchen-report':       ['view', 'print', 'export'],
  // Print module (KOT printing, crs-db fixes/060). 'print' on dineops-kds is the ticket reprint.
  'print-printers':               ['view', 'create', 'add', 'update', 'edit', 'delete', 'manage'],
  'print-agents':                 ['view', 'create', 'add', 'update', 'edit', 'delete', 'manage'],
  'print-queue':                  ['view', 'update', 'manage'],
};

const MODULE_LABELS: Record<string, string> = {
  'overview':                     'Overview',
  'dashboard':                    'Dashboard',
  'org-entity':                   'Entities',
  'org-business-unit':            'Business Units',
  'org-financial-year':           'Financial Years',
  'property-master':              'Property',
  'room-category-master':         'Room Category',
  'room-master':                  'Room',
  'meal-plan-master':             'Meal Plan',
  'addon-master':                 'Add-on',
  'property-terms-master':        'Property Terms',
  'bank-account-master':          'Bank Account',
  'tax-master':                   'Tax Masters',
  'tax-registrations':            'Tax Registrations',
  'setup-checklist':              'Setup Checklist',
  'setup-config-report':          'Configuration Report',
  'pms-charge-type':              'Charge Types',
  'pms-document-series':          'Document Series',
  'pms-print-layouts':            'Print Layouts',
  'tax-charge-mapping':           'Charge Tax Mappings',
  'config-settings':              'Settings',
  'agent-master':                 'Agent',
  'ota-master':                   'OTA',
  'corporate-master':             'Corporate',
  'rate-plan-master':             'Rate Plan',
  'booking-list':                 'Bookings',
  'front-office':                 'Front Office',
  'pms-inhouse':                  'In-House Guests',
  'pms-night-audit':              'Night Audit',
  'audit-corrections':            'Audit Corrections',
  'pms-invoices':                 'Invoices',
  'fo-closing':                   'Front Office Closing',
  'availability':                 'Availability',
  'shortage-board':               'Shortage Board',
  'guest-master':                 'Guest',
  'approval-inbox':               'Approval Inbox',
  'role-permissions':             'Role Permissions',
  'ledger-mapping':               'Ledger Mapping',
  'posting-register':             'Posting Register',
  'booking-register-report':      'Booking Register',
  'complimentary-report':         'Complimentary',
  'discount-report':              'Discount',
  'min-rate-violation-report':    'Minimum Rate Violation',
  'meal-plan-revenue-report':     'Meal Plan Revenue',
  'extra-pax-report':             'Extra Pax',
  'forward-occupancy-report':     'Forward Occupancy',
  'agent-corporate-report':       'Agent / Corporate',
  'daily-monthly-summary-report': 'Daily / Monthly Summary',
  'occupancy-chart-report':       'Room Occupancy Chart',
  'checkin-details-report':       'Check-in Details',
  'advance-payments':             'Advance Payments',
  'collection-report':            'Collection',
  'guest-ledger-report':          'Guest Ledger',
  'waiver-report':                'Relaxations & Waivers',
  'checkout-register':            'Checkout Register',
  'fo-collection-report':         'Front Office Collection',
  'meal-forecast-report':         'Meal Forecast',
  'unused-meal-report':           'Unused Package Meals',
  // Mobile PWA — labels shown on the "Mobile Access" tab
  'MOB-HOME':                     'Today (Home)',
  'MOB-GUEST':                    'Guests',
  'MOB-MEAL':                     'Meal Covers',
  'MOB-OCC':                      'Occupancy Outlook',
  'MOB-SHORT':                    'Shortage Board (mobile)',
  'MOB-APRV':                     'Approvals Inbox',
  'MOB-QBOOK':                    'Quick Booking',
  'MOB-MIS':                      'Owner Booking Summary',
  // DineOps — labels shown on the "Restaurant" tab
  'dineops-outlets':              'Outlets',
  'dineops-departments':          'Kitchen Stations',
  'dineops-kitchens':             'Kitchens (and their stations)',
  'dineops-sections':             'Sections',
  'dineops-tables':               'Tables',
  'dineops-billing-points':       'Billing Points (cash counters)',
  'dineops-quick-notes':          'Quick Notes',
  'dineops-menu':                 'Menu Items (master)',
  'dineops-outlet-items':         'Outlet Items (menu per outlet)',
  'dineops-price-board':          'Price Board (every outlet)',
  'dineops-addon-groups':         'Add-on Sets',
  'dineops-tags':                 'Tags',
  'dineops-charges':              'Charges',
  'dineops-specials':             "Today's Special",
  'dineops-price-lists':          'Price Lists',
  'dineops-orders':               'Orders (waiter app)',
  'dineops-kds':                  'Kitchen Display',
  'dineops-buffet':               'Buffet Check-off (waiter app)',
  'dineops-bills':                'Bill Queue and Settlement (cashier)',
  'dineops-shifts':               'Shifts',
  'dineops-review':               'Corrections Review',
  'dineops-day-close':            'Day Close',
  'dineops-requisitions':         'Requisitions (departments)',
  'dineops-requisition-report':   'Requisition Register',
  'dineops-bill-register':        'Bill Register',
  'dineops-approval-rules':       'Approval Rules',
  'dineops-dashboard-report':     'Dashboard',
  'dineops-sales-report':         'Sales Report',
  'dineops-settlement-report':    'Settlement Report',
  'dineops-wastage-report':       'Wastage Report',
  'dineops-complaints-report':    'Complaints Report',
  'dineops-kitchen-report':       'Kitchen Performance Report',
  'print-printers':               'Printers',
  'print-agents':                 'Print Agents',
  'print-queue':                  'Print Queue',
};

interface NavSection {
  section: string;
  modules: string[];
}

/** Same order and sections as the sidebar (login-interface.tsx navigationMenu, 2026-10-27 layout). */
const WEB_NAV_SECTIONS: NavSection[] = [
  { section: 'Dashboard',         modules: ['overview', 'dashboard'] },
  { section: 'Front Office',      modules: ['booking-list', 'availability', 'shortage-board', 'front-office', 'pms-inhouse', 'pms-invoices', 'pms-night-audit', 'audit-corrections', 'fo-closing'] },
  { section: 'Approvals',         modules: ['approval-inbox'] },
  { section: 'Masters',           modules: ['guest-master', 'agent-master', 'ota-master', 'corporate-master', 'rate-plan-master'] },
  { section: 'Reports',           modules: ['booking-register-report', 'occupancy-chart-report', 'forward-occupancy-report', 'checkin-details-report', 'checkout-register', 'daily-monthly-summary-report', 'agent-corporate-report', 'complimentary-report', 'discount-report', 'min-rate-violation-report', 'waiver-report', 'meal-plan-revenue-report', 'meal-forecast-report', 'unused-meal-report', 'extra-pax-report', 'collection-report', 'fo-collection-report', 'advance-payments', 'guest-ledger-report', 'posting-register'] },
  { section: 'Setup: Organisation',     modules: ['setup-checklist', 'org-entity', 'org-business-unit', 'org-financial-year'] },
  { section: 'Setup: Property & Rooms', modules: ['property-master', 'room-category-master', 'room-master', 'meal-plan-master', 'addon-master', 'property-terms-master', 'bank-account-master'] },
  { section: 'Setup: Tax & Billing',    modules: ['tax-master', 'tax-registrations', 'pms-charge-type', 'tax-charge-mapping', 'pms-document-series', 'pms-print-layouts', 'ledger-mapping'] },
  { section: 'Setup: Policies & Access', modules: ['config-settings', 'role-permissions', 'setup-config-report'] },
];

/**
 * Mobile PWA modules — rendered on the "Mobile Access" tab. Kept in the same
 * modal (not a separate screen) because the DB stores WEB + MOB codes in a
 * single admin.tbl_role_permissions row per role, and the tab is purely a
 * visual grouping.
 */
const MOBILE_NAV_SECTIONS: NavSection[] = [
  { section: 'Mobile App',        modules: ['MOB-HOME', 'MOB-GUEST', 'MOB-MEAL', 'MOB-OCC', 'MOB-SHORT', 'MOB-APRV', 'MOB-QBOOK', 'MOB-MIS'] },
];

/**
 * DineOps modules — rendered on the "Restaurant" tab: the restaurant screens in
 * crs-web, the waiter app (Orders) and the kitchen display. Same row and
 * columns as the web and mobile modules; the tab is a visual grouping.
 */
const RESTAURANT_NAV_SECTIONS: NavSection[] = [
  { section: 'Restaurant Setup',  modules: ['dineops-outlets', 'dineops-kitchens', 'dineops-sections', 'dineops-tables', 'dineops-billing-points', 'dineops-approval-rules', 'dineops-quick-notes'] },
  { section: 'Restaurant Menu',   modules: ['dineops-menu', 'dineops-outlet-items', 'dineops-price-board', 'dineops-addon-groups', 'dineops-tags', 'dineops-charges', 'dineops-specials', 'dineops-price-lists'] },
  { section: 'Restaurant Service', modules: ['dineops-orders', 'dineops-kds', 'dineops-departments', 'dineops-buffet', 'dineops-requisitions', 'dineops-bills', 'dineops-shifts', 'dineops-review', 'dineops-day-close'] },
  { section: 'Restaurant Reports', modules: ['dineops-dashboard-report', 'dineops-sales-report', 'dineops-settlement-report', 'dineops-wastage-report', 'dineops-complaints-report', 'dineops-kitchen-report', 'dineops-requisition-report', 'dineops-bill-register'] },
  { section: 'Printing',          modules: ['print-printers', 'print-agents', 'print-queue'] },
];

type PermissionTab = 'web' | 'mobile' | 'restaurant';

const TAB_LABELS: Record<PermissionTab, string> = {
  web:        'Web Access',
  mobile:     'Mobile Access',
  restaurant: 'Restaurant',
};

const TAB_SECTIONS: Record<PermissionTab, NavSection[]> = {
  web:        WEB_NAV_SECTIONS,
  mobile:     MOBILE_NAV_SECTIONS,
  restaurant: RESTAURANT_NAV_SECTIONS,
};

/** Modules of a tab the role can see — the badge on each tab. */
const countOn = (tab: PermissionTab, visible: string[]) =>
  TAB_SECTIONS[tab].flatMap((s) => s.modules).filter((m) => visible.includes(m)).length;

// ============================================================================
// FORM MODAL (Create / Edit)
// ============================================================================

interface RolePermissionFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: RolePermissionFormData;
  isSystemRole?: boolean;
  /**
   * TRUE when `formData` differs from the snapshot taken at modal-open time.
   * When FALSE, the Save button is disabled — preventing the no-op API call
   * the user reported (clicking Save on a locked crs_system row was triggering
   * an UPDATE even when nothing changed). Defaults to TRUE for back-compat
   * with callers that don't track dirty state yet.
   */
  isDirty?: boolean;
  onFormDataChange: (data: RolePermissionFormData) => void;
  onSave: () => void;
  onClose: () => void;
  /** Restaurant tab → Outlet access: the outlets and users to pick from, loaded by the screen. */
  outletOptions?: AccessOption[];
  stationOptions?: AccessOption[];
  outletUsers?: RoleOutletUser[];
  outletAccessLoading?: boolean;
  outletAccessError?: string;
}

export const RolePermissionFormModal: React.FC<RolePermissionFormModalProps> = ({
  isOpen,
  mode,
  formData,
  isSystemRole = false,
  isDirty = true,
  onFormDataChange,
  onSave,
  onClose,
  outletOptions = [],
  stationOptions = [],
  outletUsers = [],
  outletAccessLoading = false,
  outletAccessError,
}) => {
  const [activeTab, setActiveTab] = React.useState<PermissionTab>('web');

  if (!isOpen) return null;

  const title = mode === 'create' ? 'Create Role Permission' : 'Edit Role Permission';
  const isCodeReadOnly = mode === 'edit' || isSystemRole;
  // Selected outlets with none ticked would lock every holder out: not savable.
  const outletAccessValid = (formData.outletAccess?.accessMode !== 'SELECTED' || formData.outletAccess.ids.length > 0)
    && (formData.stationAccess?.accessMode !== 'SELECTED' || formData.stationAccess.ids.length > 0);
  const isValid = formData.roleCode.trim() !== '' && formData.roleName.trim() !== '' && outletAccessValid;

  const activeSections = TAB_SECTIONS[activeTab];

  // Badge counts — helps the admin see at a glance which tab has permissions
  // configured, since a user might grant only web, mobile or restaurant access.
  const tabCounts: Record<PermissionTab, number> = {
    web: countOn('web', formData.visibleModules),
    mobile: countOn('mobile', formData.visibleModules),
    restaurant: countOn('restaurant', formData.visibleModules),
  };

  // Save is disabled when the form is invalid OR (in edit mode) when nothing
  // has changed since the modal opened. Create mode is allowed even when
  // technically "dirty" is false on first paint — the user must fill in
  // required fields, which always makes formData differ from the snapshot.
  const canSave = isValid && (mode === 'create' || isDirty);

  /**
   * The supreme `crs_system` role must always retain full access. Editing its
   * permission matrix is disabled to prevent admin lockout — by definition,
   * crs_system is dev/support full unrestricted access. To narrow access for
   * a specific user, use a different role (or create a custom one).
   * role_name and description remain editable for organizational notes.
   */
  const isSupremeSystem = formData.roleCode.trim().toLowerCase() === 'crs_system';

  const isModuleVisible = (moduleId: string) => formData.visibleModules.includes(moduleId);
  const isActionChecked = (moduleId: string, action: string) =>
    formData.moduleActions[moduleId]?.includes(action) ?? false;

  const toggleModuleVisibility = (moduleId: string) => {
    const wasVisible = isModuleVisible(moduleId);
    const newVisibleModules = wasVisible
      ? formData.visibleModules.filter((m) => m !== moduleId)
      : [...formData.visibleModules, moduleId];

    const newModuleActions = { ...formData.moduleActions };
    if (wasVisible) {
      // Turning OFF — clear actions for this module
      delete newModuleActions[moduleId];
    }
    onFormDataChange({ ...formData, visibleModules: newVisibleModules, moduleActions: newModuleActions });
  };

  const toggleAction = (moduleId: string, action: string) => {
    const current = formData.moduleActions[moduleId] || [];
    const newActions = current.includes(action)
      ? current.filter((a) => a !== action)
      : [...current, action];

    const newModuleActions = { ...formData.moduleActions };
    if (newActions.length > 0) {
      newModuleActions[moduleId] = newActions;
    } else {
      delete newModuleActions[moduleId];
    }
    onFormDataChange({ ...formData, moduleActions: newModuleActions });
  };

  const toggleAllInSection = (modules: string[], selectAll: boolean) => {
    const newVisibleModules = [...formData.visibleModules];
    const newModuleActions = { ...formData.moduleActions };

    for (const moduleId of modules) {
      if (selectAll) {
        if (!newVisibleModules.includes(moduleId)) newVisibleModules.push(moduleId);
        newModuleActions[moduleId] = [...(MODULE_AVAILABLE_ACTIONS[moduleId] || [])];
      } else {
        const idx = newVisibleModules.indexOf(moduleId);
        if (idx >= 0) newVisibleModules.splice(idx, 1);
        delete newModuleActions[moduleId];
      }
    }
    onFormDataChange({ ...formData, visibleModules: newVisibleModules, moduleActions: newModuleActions });
  };

  const isSectionFullySelected = (modules: string[]) =>
    modules.every((m) => isModuleVisible(m));

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Role Info */}
          <div className="form-grid-3" style={{ marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Role Code *</label>
              <input
                className="form-input"
                value={formData.roleCode}
                onChange={(e) => onFormDataChange({ ...formData, roleCode: e.target.value })}
                placeholder="e.g. crs_manager"
                disabled={isCodeReadOnly}
                style={isCodeReadOnly ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role Name *</label>
              <input
                className="form-input"
                value={formData.roleName}
                onChange={(e) => onFormDataChange({ ...formData, roleName: e.target.value })}
                placeholder="e.g. Property Manager"
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                Priority
                <span
                  title="Lower number wins. Baseline: system=0, admin=10, booking-agent=40, user=100. Custom roles default to 1000."
                  style={{ marginLeft: '0.35rem', color: 'var(--text-muted)', cursor: 'help' }}
                >
                  <IconSystem.info size={12} />
                </span>
              </label>
              <input
                className="form-input"
                type="number"
                min={0}
                step={10}
                value={formData.priority}
                onChange={(e) => {
                  const parsed = Number.parseInt(e.target.value, 10);
                  onFormDataChange({
                    ...formData,
                    priority: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
                  });
                }}
                placeholder="1000"
                // crs_system priority is locked to 0 — the immutable supreme role
                // must always win the "highest priority" resolver. Server-side
                // guards enforce this too (see RolePermissionService.UpdateAsync).
                disabled={isSupremeSystem}
                style={isSupremeSystem ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                title={
                  isSupremeSystem
                    ? "The 'crs_system' role priority is locked at 0 (supreme role)."
                    : undefined
                }
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
              placeholder="Describe the role's purpose…"
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Permission Matrix */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Permission Matrix
            </h3>

            {/* Read-only banner shown when editing the supreme crs_system role */}
            {isSupremeSystem && (
              <div
                role="status"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  padding: '0.6rem 0.75rem',
                  marginBottom: '1rem',
                  borderRadius: '6px',
                  background: 'rgba(99, 102, 241, 0.10)',
                  border: '1px solid rgba(99, 102, 241, 0.35)',
                  color: 'var(--text-primary)',
                  fontSize: '0.8125rem',
                }}
              >
                <IconSystem.lock size={16} />
                <span>
                  System role — permissions cannot be modified. To restrict access, use other roles instead.
                </span>
              </div>
            )}

            {/* Tabs — Web / Mobile. Same form state; visual grouping only. */}
            <div
              role="tablist"
              aria-label="Permission scope"
              style={{
                display: 'flex',
                gap: '0.25rem',
                borderBottom: '1px solid var(--border-color)',
                marginBottom: '1rem',
              }}
            >
              {(['web', 'mobile', 'restaurant'] as PermissionTab[]).map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      appearance: 'none',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${isActive ? 'var(--primary, #2563eb)' : 'transparent'}`,
                      padding: '0.5rem 0.9rem',
                      marginBottom: '-1px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    {TAB_LABELS[tab]}
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '18px',
                        padding: '0 6px',
                        borderRadius: '9px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: tabCounts[tab] > 0 ? 'rgba(99, 102, 241, 0.15)' : 'var(--bg-secondary, rgba(255,255,255,0.05))',
                        color: tabCounts[tab] > 0 ? '#818cf8' : 'var(--text-muted)',
                      }}
                    >
                      {tabCounts[tab]}
                    </span>
                  </button>
                );
              })}
            </div>

            {activeTab === 'restaurant' && (
              <RoleOutletAccessSection
                roleCode={formData.roleCode}
                kind="outlet"
                value={formData.outletAccess}
                options={outletOptions}
                users={outletUsers}
                loading={outletAccessLoading}
                error={outletAccessError}
                locked={isSupremeSystem}
                onChange={(outletAccess) => onFormDataChange({ ...formData, outletAccess })}
              />
            )}
            {activeTab === 'restaurant' && (
              <RoleOutletAccessSection
                kind="station"
                roleCode={formData.roleCode}
                value={formData.stationAccess}
                options={stationOptions}
                users={outletUsers}
                loading={outletAccessLoading}
                error={outletAccessError}
                locked={isSupremeSystem}
                onChange={(stationAccess) => onFormDataChange({ ...formData, stationAccess })}
              />
            )}

            {activeSections.map((section) => {
              const allSelected = isSectionFullySelected(section.modules);

              return (
                <div key={section.section} style={{ marginBottom: '1.25rem' }}>
                  {/* Section header with select-all toggle */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                      borderRadius: '6px',
                      cursor: isSupremeSystem ? 'not-allowed' : 'pointer',
                      opacity: isSupremeSystem ? 0.7 : 1,
                    }}
                    onClick={isSupremeSystem ? undefined : () => toggleAllInSection(section.modules, !allSelected)}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleAllInSection(section.modules, !allSelected)}
                      disabled={isSupremeSystem}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {section.section}
                    </span>
                  </div>

                  {/* Module rows */}
                  {section.modules.map((moduleId) => {
                    const visible = isModuleVisible(moduleId);
                    const availableActions = MODULE_AVAILABLE_ACTIONS[moduleId] || [];

                    return (
                      <div
                        key={moduleId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.35rem 0.6rem 0.35rem 1.8rem',
                          gap: '0.75rem',
                          opacity: visible ? 1 : 0.5,
                        }}
                      >
                        {/* Visibility toggle */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            minWidth: '200px',
                            cursor: isSupremeSystem ? 'not-allowed' : 'pointer',
                            fontSize: '0.82rem',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={() => toggleModuleVisibility(moduleId)}
                            disabled={isSupremeSystem}
                            style={{ accentColor: 'var(--primary)' }}
                          />
                          {MODULE_LABELS[moduleId] || moduleId}
                        </label>

                        {/* Action checkboxes */}
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                          {availableActions.map((action) => (
                            <label
                              key={action}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.75rem',
                                color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                                cursor: visible && !isSupremeSystem ? 'pointer' : 'not-allowed',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isActionChecked(moduleId, action)}
                                onChange={() => toggleAction(moduleId, action)}
                                disabled={!visible || isSupremeSystem}
                                style={{ accentColor: 'var(--primary)' }}
                              />
                              {action}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={!canSave}
            title={!outletAccessValid ? 'Restaurant tab: tick at least one outlet / kitchen station for a Selected choice'
              : !isValid ? 'Role Code and Role Name are required'
              : (!isDirty && mode === 'edit' ? 'No changes to save' : undefined)}
          >
            <IconSystem.save size={16} />
            {' '}{mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// VIEW MODAL (Read-Only)
// ============================================================================

interface RolePermissionViewModalProps {
  isOpen: boolean;
  record: RolePermissionRecord | null;
  onClose: () => void;
}

export const RolePermissionViewModal: React.FC<RolePermissionViewModalProps> = ({ isOpen, record, onClose }) => {
  const [activeTab, setActiveTab] = React.useState<PermissionTab>('web');

  if (!isOpen || !record) return null;

  const detailStyle: React.CSSProperties = { fontSize: '0.82rem', color: 'var(--text-secondary)' };
  const labelStyle: React.CSSProperties  = { fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.15rem' };

  const activeSections = TAB_SECTIONS[activeTab];
  const tabCounts: Record<PermissionTab, number> = {
    web: countOn('web', record.visibleModules),
    mobile: countOn('mobile', record.visibleModules),
    restaurant: countOn('restaurant', record.visibleModules),
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">Role Permission Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Role info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <div style={labelStyle}>Role Code</div>
              <div style={detailStyle}>{record.roleCode}</div>
            </div>
            <div>
              <div style={labelStyle}>Role Name</div>
              <div style={detailStyle}>{record.roleName}</div>
            </div>
            <div>
              <div style={labelStyle}>Type</div>
              <div style={detailStyle}>
                <span style={{
                  padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem',
                  background: record.isSystemRole ? 'rgba(99,102,241,0.15)' : 'rgba(156,163,175,0.15)',
                  color: record.isSystemRole ? '#818cf8' : '#9ca3af',
                }}>
                  {record.isSystemRole ? 'System' : 'Custom'}
                </span>
              </div>
            </div>
            <div>
              <div style={labelStyle}>Status</div>
              <div style={detailStyle}>
                <span className={`badge badge-${record.isActive ? 'active' : 'inactive'}`}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {record.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={labelStyle}>Description</div>
                <div style={detailStyle}>{record.description}</div>
              </div>
            )}
          </div>

          {/* Permission matrix (read-only) */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Permission Matrix
            </h3>

            {/* Tabs — mirror the edit modal so the two views feel consistent. */}
            <div
              role="tablist"
              aria-label="Permission scope"
              style={{
                display: 'flex',
                gap: '0.25rem',
                borderBottom: '1px solid var(--border-color)',
                marginBottom: '1rem',
              }}
            >
              {(['web', 'mobile', 'restaurant'] as PermissionTab[]).map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      appearance: 'none',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${isActive ? 'var(--primary, #2563eb)' : 'transparent'}`,
                      padding: '0.5rem 0.9rem',
                      marginBottom: '-1px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    {TAB_LABELS[tab]}
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '18px',
                        padding: '0 6px',
                        borderRadius: '9px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: tabCounts[tab] > 0 ? 'rgba(99, 102, 241, 0.15)' : 'var(--bg-secondary, rgba(255,255,255,0.05))',
                        color: tabCounts[tab] > 0 ? '#818cf8' : 'var(--text-muted)',
                      }}
                    >
                      {tabCounts[tab]}
                    </span>
                  </button>
                );
              })}
            </div>

            {activeSections.map((section) => {
              const visibleInSection = section.modules.filter((m) => record.visibleModules.includes(m));
              if (visibleInSection.length === 0) return null;

              return (
                <div key={section.section} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)',
                    padding: '0.3rem 0.6rem',
                    background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                    borderRadius: '6px', marginBottom: '0.4rem',
                  }}>
                    {section.section}
                  </div>

                  {visibleInSection.map((moduleId) => {
                    const actions = record.moduleActions[moduleId] || [];
                    return (
                      <div
                        key={moduleId}
                        style={{ display: 'flex', alignItems: 'center', padding: '0.3rem 0.6rem 0.3rem 1.8rem', gap: '0.75rem' }}
                      >
                        <span style={{ fontSize: '0.82rem', minWidth: '200px', color: 'var(--text-secondary)' }}>
                          {MODULE_LABELS[moduleId] || moduleId}
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {actions.map((action) => (
                            <span
                              key={action}
                              style={{
                                padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.7rem',
                                background: 'rgba(99,102,241,0.12)', color: '#818cf8',
                              }}
                            >
                              {action}
                            </span>
                          ))}
                          {actions.length === 0 && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>visible only</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
